import { ChangeDetectionStrategy, Component, OnInit, Signal, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MaskitoDirective } from '@maskito/angular';
import { provideIcons } from '@ng-icons/core';
import { lucideGoal } from '@ng-icons/lucide';

import { TextInputComponent } from 'app/home/common/components/forms/text-input/text-input.component';
import { utilToIsoDate } from 'app/home/common/utils/iso-date-type/to-iso-date';
import { BaseQuoteForm } from 'app/home/create-quote/form-config/quote-form-base';
import { QuoteFormContextDecorator } from 'app/home/create-quote/form-config/quote-form-decorator';
import { Lockable, LockedQuoteAlertComponent, WithLockableFields } from 'app/home/create-quote/form-locking';
import { CoApplicantInfoQuoteFormInput } from 'app/home/create-quote/forms/co-applicant-info/co-applicant-info.types';
import { ageValidator } from 'app/home/create-quote/forms/shared/age.validator';
import mask from 'app/home/create-quote/forms/shared/date-mask-options';
import { FormCardComponent } from 'app/home/create-quote/forms/shared/form-card.component';
import { ContactDialogService } from 'app/home/create-quote/services/contact-dialog/contact-dialog.service';
import { QuoteDraftState } from 'app/home/global-store/quote-draft/quote-draft.model';
import { GoogleTagManagerService, SegmentEvent } from 'app/shared/services/analytics/google-tagmanager/gtm.service';

@Component({
  selector: 'app-co-applicant-info',
  imports: [ReactiveFormsModule, TextInputComponent, FormCardComponent, MaskitoDirective, LockedQuoteAlertComponent],
  providers: [provideIcons({ lucideGoal })],
  templateUrl: './co-applicant-info.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `
      :host {
        display: contents;
      }
    `,
  ],
})
@QuoteFormContextDecorator({
  legacyShape: '/quote/co-applicant-info',
})
export class QuoteFormCoApplicantInfoComponent extends BaseQuoteForm<CoApplicantInfoQuoteFormInput> implements OnInit, Lockable {
  protected contactDialogService = inject(ContactDialogService);
  private gtmService = inject(GoogleTagManagerService);

  public formDataIn: CoApplicantInfoQuoteFormInput = this.quoteDraftStore.coApplicantInfoQuoteFormSelector();

  readonly maskitoDateOptions = mask;

  public isLocked: Signal<{ [key: string]: boolean }> = WithLockableFields.createIsLocked.call(this);
  public isFieldLocked = WithLockableFields.isFieldLocked;
  public allFieldsLocked = WithLockableFields.allFieldsLocked;

  public getFormControls() {
    return this.coApplicantInfoForm.controls;
  }

  public formDefinition = {
    firstName: new FormControl<string>('', {
      validators: [Validators.required],
      updateOn: 'blur',
    }),
    lastName: new FormControl<string>('', {
      validators: [Validators.required],
      updateOn: 'blur',
    }),
    dob: new FormControl<string>('', {
      validators: [Validators.required, Validators.minLength(10), (control) => ageValidator(control, () => this.quoteDraftStore.address()?.state)],
      updateOn: 'blur',
    }),
  };

  public coApplicantInfoForm = new FormGroup(this.formDefinition);

  public errorMessages = {
    firstName: {
      required: 'Co-applicant first name is required',
    },
    lastName: {
      required: 'Co-applicant last name is required',
    },
    dob: {
      required: 'Co-applicant date of birth is required',
      minlength: 'Please enter a valid date in MM/DD/YYYY format',
      invalidDateFormat: 'Please enter a valid date in MM/DD/YYYY format',
      underage: 'Birthdate is outside the eligible range',
      overMaxAge: 'Birthdate is outside the eligible range',
    },
  };

  public ngOnInit() {
    if (this.formDataIn) {
      this.coApplicantInfoForm.patchValue(this.formDataIn);
    }

    this.gtmService.sendDataLayerEventWithQuoteData(SegmentEvent.CO_APPLICANT_PAGE_VIEWED);
  }

  private getFormValues(): Pick<QuoteDraftState, 'coApplicant'> {
    const { controls } = this.coApplicantInfoForm;
    const dobValue = controls.dob.value;
    return {
      coApplicant: {
        firstName: controls.firstName.value ?? '',
        lastName: controls.lastName.value ?? '',
        dob: dobValue ? utilToIsoDate(dobValue) : undefined,
      },
    };
  }

  public handleBlur() {
    this.gtmService.sendDataLayerEventWithQuoteData(SegmentEvent.CO_APPLICANT_PAGE_MODIFIED, this.getFormValues());
  }

  public handleSubmit(e: Event) {
    e.preventDefault();
    this.coApplicantInfoForm.markAllAsTouched();
    if (this.coApplicantInfoForm.valid) {
      if (this.allFieldsLocked()) {
        super.navigateForward();
      } else {
        this.gtmService.sendDataLayerEventWithQuoteData(SegmentEvent.CO_APPLICANT_PAGE_SUBMITTED, this.getFormValues());
        super.saveFormData(this.getFormValues());
      }
    }
  }
}
